import { useEffect, useRef, useState } from "react";
import type { ActivityEvent, Alert, DealSummary, TaskItem } from "@edge/shared";
import {
  ApiError,
  dismissWatchtowerAlert,
  fetchActivity,
  fetchCrmTasks,
  fetchDealsStrip,
  fetchWatchtower,
} from "../api";

export type LoadState = "idle" | "loading" | "ok" | "error";

const UNDO_WINDOW_MS = 5000;

/**
 * A dismissal the user has initiated but that hasn't yet committed to the
 * server. Held for `UNDO_WINDOW_MS` so the user can take it back from the
 * undo toast. Stored in state (drives the toast stack) and mirrored to a
 * ref-held timer Map (drives the deferred commit).
 */
export interface PendingDismissal {
  alert: Alert;
  kind: "solved" | "removed";
  note?: string;
  /** Millis-since-epoch at which the timer will fire. Toast countdown uses this. */
  expiresAt: number;
}

interface CockpitData {
  deals: DealSummary[];
  crmTasks: TaskItem[];
  activity: ActivityEvent[];
  /** Count of activity events suppressed by noise filter — drives the banner. */
  activityNoiseSuppressed: number;
  /** Visible alerts — pending dismissals are filtered out so they hide
   *  immediately, but they aren't truly gone until the undo window expires. */
  alerts: Alert[];
  /** In-flight dismissals waiting on the undo window. Newest first. */
  pendingDismissals: PendingDismissal[];
  /** Notes the user typed for a dismissal they then undid. Keyed by alert id;
   *  AlertsRail uses this to pre-populate the chevron panel when re-opened
   *  so the user doesn't have to retype. Cleared on next dismiss of that id. */
  restoredNotes: Map<string, { kind: "solved" | "removed"; note: string }>;
  /** Tracks the watchtower's own loading state separately — it's the slowest call (~10–20s). */
  watchtowerLoading: boolean;
  loadState: LoadState;
  error: string | null;
  /** Last commit-time error message — surfaces in the App-level error toast. */
  dismissError: string | null;
  /** Clears the commit-time error toast (e.g. after user dismisses it). */
  clearDismissError: () => void;
  /** Re-fetch the cockpit. Watchtower is allowed to serve from its 30-min cache. */
  reload: () => void;
  /** Force a fresh Watchtower classifier run, bypassing the 30-min cache. The
   *  Watchtower panel's refresh button uses this — `reload()` doesn't, so
   *  cockpit-incidental reloads (e.g. after a CRM task complete) don't burn
   *  a fresh Sonnet classification every time. */
  refreshWatchtower: () => void;
  /** Stage a dismissal: hides the alert immediately, fires the POST after
   *  the undo window expires. Returns void — there's nothing to await. */
  dismissAlert: (
    alertId: string,
    kind: "solved" | "removed",
    note?: string,
  ) => void;
  /** Cancel a pending dismissal during the undo window. Restores the alert. */
  undoDismiss: (alertId: string) => void;
}

/**
 * Loads real Zoho + Microsoft Graph data for the home cockpit. Each fetch is
 * resolved with `Promise.allSettled` so a partial outage (e.g. Microsoft) still
 * fills the panels that did succeed.
 */
export function useCockpitData(): CockpitData {
  const [deals, setDeals] = useState<DealSummary[]>([]);
  const [crmTasks, setCrmTasks] = useState<TaskItem[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [activityNoiseSuppressed, setActivityNoiseSuppressed] = useState(0);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [pendingDismissals, setPendingDismissals] = useState<PendingDismissal[]>([]);
  const [restoredNotes, setRestoredNotes] = useState<
    Map<string, { kind: "solved" | "removed"; note: string }>
  >(new Map());
  const [dismissError, setDismissError] = useState<string | null>(null);
  // Timer handles for in-flight commits, keyed by alert id. Held in a ref so
  // the dismiss/undo handlers operate against a synchronous, always-current
  // source — avoids stale closures when timers fire long after their setup.
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const [watchtowerLoading, setWatchtowerLoading] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  /** Separate signal so the refresh button can force a fresh WT run without
   *  re-fetching deals/tasks/activity. Increments toggle the effect's `force`
   *  path. */
  const [watchtowerForceTick, setWatchtowerForceTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    setError(null);

    // Fast trio (deals + tasks + activity) drives the page-load state.
    Promise.allSettled([
      fetchDealsStrip(),
      fetchCrmTasks(),
      fetchActivity({ limit: 10 }),
    ]).then(
      ([dealsR, tasksR, actR]) => {
        if (cancelled) return;
        if (dealsR.status === "fulfilled") setDeals(dealsR.value);
        if (tasksR.status === "fulfilled") setCrmTasks(tasksR.value);
        if (actR.status === "fulfilled") {
          setActivity(actR.value.events);
          setActivityNoiseSuppressed(actR.value.noiseSuppressed ?? 0);
        }

        const failures = [dealsR, tasksR, actR].filter(
          (r): r is PromiseRejectedResult => r.status === "rejected",
        );
        if (failures.length === 0) {
          setLoadState("ok");
        } else {
          console.error("[edge] cockpit partial failure", failures.map((f) => f.reason));
          setError(
            failures.length === 3
              ? "all_failed"
              : `${failures.length}_of_3_failed`,
          );
          setLoadState(failures.length === 3 ? "error" : "ok");
        }
      },
    );

    // Watchtower lives on its own clock — Claude classifier ~10-20s on cold
    // cache. The `force` flag only fires when the user explicitly hits the
    // refresh button (watchtowerForceTick > 0), so routine cockpit reloads
    // (CRM task complete, deal update) honor the 30-min KV cache.
    const force = watchtowerForceTick > 0;
    setWatchtowerLoading(true);
    fetchWatchtower({ refresh: force })
      .then((r) => {
        if (cancelled) return;
        setAlerts(r.alerts);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[edge] watchtower fetch failed", err);
      })
      .finally(() => {
        if (!cancelled) setWatchtowerLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey, watchtowerForceTick]);

  // Light, focus-gated poll: refresh ONLY the activity feed (not the heavier
  // watchtower/deals/tasks) every ~3 min while the cockpit tab is visible, plus
  // on regaining focus — so deletions and new mail reflect without a manual
  // reload. Skips entirely when the tab is hidden. Manual reload() is unaffected.
  useEffect(() => {
    const POLL_MS = 3 * 60 * 1000;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      fetchActivity({ limit: 10 })
        .then((r) => {
          setActivity(r.events);
          setActivityNoiseSuppressed(r.noiseSuppressed ?? 0);
        })
        .catch(() => {
          /* transient poll failure — the next tick retries */
        });
    };
    const id = window.setInterval(tick, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Cleanup all in-flight timers on unmount. Per the spec, dismissals lost
  // to a refresh/close mid-window are accept-it for V1 — we don't try to
  // beacon them out, just clear the timer so React's strict-mode cleanup
  // doesn't leak.
  useEffect(() => {
    const timers = dismissTimersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  /**
   * Stage a dismissal. Hides the alert from the visible list immediately
   * (via pendingDismissals filtering), shows the undo toast, and fires the
   * POST after UNDO_WINDOW_MS. Until then, undoDismiss can take it back.
   *
   * If the same alert id is already pending (defensive — shouldn't happen
   * since the row is hidden), cancel the prior pending and start fresh.
   */
  const dismissAlert = (
    alertId: string,
    kind: "solved" | "removed",
    note?: string,
  ) => {
    const alert = alerts.find((a) => a.id === alertId);
    if (!alert) return;

    // Cancel any prior pending for this id so we don't double-fire.
    const existing = dismissTimersRef.current.get(alertId);
    if (existing) clearTimeout(existing);

    // User initiated a fresh dismiss — drop any restored-note memory for
    // this alert. If they undo this one too, the *new* note (if any) will
    // be remembered instead.
    setRestoredNotes((cur) => {
      if (!cur.has(alertId)) return cur;
      const next = new Map(cur);
      next.delete(alertId);
      return next;
    });

    const expiresAt = Date.now() + UNDO_WINDOW_MS;
    setPendingDismissals((cur) => [
      { alert, kind, note, expiresAt },
      ...cur.filter((p) => p.alert.id !== alertId),
    ]);

    const timer = setTimeout(() => {
      void commitDismissal(alertId, kind, note);
    }, UNDO_WINDOW_MS);
    dismissTimersRef.current.set(alertId, timer);
  };

  /**
   * Cancel a pending dismissal. Restores the alert to the visible list and,
   * if a note had been typed, remembers it under restoredNotes so the user
   * can re-open the chevron panel without retyping.
   */
  const undoDismiss = (alertId: string) => {
    const timer = dismissTimersRef.current.get(alertId);
    if (timer) clearTimeout(timer);
    dismissTimersRef.current.delete(alertId);

    setPendingDismissals((cur) => {
      const found = cur.find((p) => p.alert.id === alertId);
      if (found && found.note && found.note.trim()) {
        setRestoredNotes((rn) => {
          const next = new Map(rn);
          next.set(alertId, { kind: found.kind, note: found.note! });
          return next;
        });
      }
      return cur.filter((p) => p.alert.id !== alertId);
    });
  };

  /**
   * Timer fire: undo window expired. Remove from pending, remove from the
   * source alerts list, POST to the server. 409 → success no-op (cross-tab
   * race or already-dismissed). Other errors → restore the alert to the list
   * AND surface a dismiss error toast — the user is past the undo window so
   * the reappearance can otherwise feel like a ghost.
   */
  const commitDismissal = async (
    alertId: string,
    kind: "solved" | "removed",
    note?: string,
  ) => {
    dismissTimersRef.current.delete(alertId);

    let toCommit: PendingDismissal | undefined;
    setPendingDismissals((cur) => {
      toCommit = cur.find((p) => p.alert.id === alertId);
      return cur.filter((p) => p.alert.id !== alertId);
    });
    if (!toCommit) {
      // Was undone between timer fire and microtask — nothing to do.
      return;
    }

    setAlerts((cur) => cur.filter((a) => a.id !== alertId));

    try {
      await dismissWatchtowerAlert(alertId, {
        kind,
        ...(note && note.trim() ? { note: note.trim() } : {}),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Already gone server-side; local state matches.
        return;
      }
      console.error("[edge] dismiss commit failed", err);
      setAlerts((cur) => [toCommit!.alert, ...cur]);
      setDismissError(
        `Couldn't save dismissal for "${toCommit.alert.title}" — please try again.`,
      );
    }
  };

  // Visible alerts: filter out anything currently in the undo window.
  const pendingIds = new Set(pendingDismissals.map((p) => p.alert.id));
  const visibleAlerts = pendingIds.size === 0
    ? alerts
    : alerts.filter((a) => !pendingIds.has(a.id));

  return {
    deals,
    crmTasks,
    activity,
    activityNoiseSuppressed,
    alerts: visibleAlerts,
    pendingDismissals,
    restoredNotes,
    watchtowerLoading,
    loadState,
    error,
    dismissError,
    clearDismissError: () => setDismissError(null),
    reload: () => setReloadKey((k) => k + 1),
    refreshWatchtower: () => setWatchtowerForceTick((t) => t + 1),
    dismissAlert,
    undoDismiss,
  };
}
